import { now } from '../lib/dateTime';

/**
 * Creates and returns a debounced version of the passed function that will postpone its execution until the delay time have elapsed since the last time it was invoked.
 *
 * The first call will always be executed without delay.
 */
export const debounce = <T extends (...args: unknown[]) => void>(
  fn: T,
  delayMs: number
) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  let lastCallTime: number | undefined;

  const cancel = () => {
    clearTimeout(timeoutId);
    lastCallTime = undefined;
  };

  async function debounced(this: ThisParameterType<T>, ...args: Parameters<T>) {
    const currentTime = await now();

    const callFn = () => {
      fn.apply(this, args);
      lastCallTime = currentTime;
    };

    // First call
    if (lastCallTime === undefined) {
      callFn();
    } else {
      const remainingTime = currentTime - lastCallTime;

      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        callFn();
      }, delayMs - remainingTime);
    }
  }

  debounced.cancel = cancel;

  return debounced;
};
